import React, { useContext, useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { Box } from '@mui/material';

import Button from 'components/common/Button/Button';
import { CloseIcon, DoneDarkIcon, EditIcon, TooltipIcon } from 'components/common/Icons/Icons';
import Table from 'components/common/Table/Table';
import TableBody from 'components/common/Table/TableBody/TableBody';
import TableBodyCell from 'components/common/Table/TableBodyCell/TableBodyCell';
import TableHead from 'components/common/Table/TableHead/TableHead';
import TableHeadCell from 'components/common/Table/TableHeadCell/TableHeadCell';
import TableRow from 'components/common/Table/TableRow/TableRow';
import Tooltip from 'components/common/Tooltip/Tooltip';
import Checkbox from 'components/common/Checkbox/Checkbox';
import { RootStoreContext } from 'index';
import { capitalize } from 'common/utils/string.utils';
import { ApplicationRoute } from 'routes/routes.enums';
import Input from 'components/common/Input/Input';
import {
  EMAIL_EVENTS_TOOLTIPS,
  MAP_STATUS_LABEL_TO_EMAIL_NOTIFICATION_EVENT,
  STATUS_LABELS,
} from 'common/constants/emailNotifications.constants';
import { StatusLabel } from 'common/enums/emailNotifications.enums';

import styles from './CompanyNotifications.module.scss';
import { getEmailNotificationLabel } from './companyNotifications.utils';
import { EmailNotifications } from './companyNotifications.types';
import { validateEmail } from '../auth/authValidation.utils';

const CompanyEmailTooltip = () => {
  return (
    <Tooltip
      title={
        <Box>
          This email address can be only changed on{' '}
          <Link to={ApplicationRoute.CompanyProfile} className={styles.tooltipLink}>
            Company Profile
          </Link>{' '}
          page.
        </Box>
      }
      arrow
      disableInteractive={false}
    >
      <TooltipIcon />
    </Tooltip>
  );
};

const EditableNotificationRow = observer(
  ({
    item,
    rowIndex,
    handleChangeEmail,
    handleToggleEvent,
  }: {
    item: EmailNotifications;
    rowIndex: number;
    handleChangeEmail: (rowIndex: number, newEmail: string) => void;
    handleToggleEvent: (
      rowIndex: number,
      emailNotificationEvent: keyof typeof MAP_STATUS_LABEL_TO_EMAIL_NOTIFICATION_EVENT,
    ) => void;
  }) => {
    const emailError = item.email ? validateEmail(item.email, 'Email', rowIndex === 0) : '';
    const shouldDisableCheckboxes = Boolean(emailError);

    return (
      <TableRow className={clsx(styles.tableRowTemplate, styles.tableRowTemplate_editMode)}>
        <TableBodyCell className={clsx(styles.bodyCell, styles.bodyCell_editMode)}>
          <div>
            <span className={clsx(styles.emailCellHeader, styles.emailCellHeader_editMode)}>
              <span className={styles.emailCellTitle}>{getEmailNotificationLabel(rowIndex)}</span>
              {item.isCompanyEmail && <CompanyEmailTooltip />}
            </span>
            <br />
            <Input
              className={styles.emailInput}
              value={item.email}
              disabled={item.isCompanyEmail}
              onChange={(event) => !item.isCompanyEmail && handleChangeEmail(rowIndex, event.target.value)}
            />
            {emailError && <div className={styles.errorText}>{emailError}</div>}
          </div>
        </TableBodyCell>

        {STATUS_LABELS.map((statusLabel) => {
          const eventType = MAP_STATUS_LABEL_TO_EMAIL_NOTIFICATION_EVENT[statusLabel];
          const isEventEnabled = item.events.includes(eventType);

          return (
            <TableBodyCell key={statusLabel} className={styles.bodyCell}>
              <Checkbox
                isChecked={isEventEnabled}
                onChange={() => handleToggleEvent(rowIndex, statusLabel)}
                disabled={shouldDisableCheckboxes}
              />
            </TableBodyCell>
          );
        })}
      </TableRow>
    );
  },
);

const NotificationRow = observer(({ item, rowIndex }: { item: EmailNotifications; rowIndex: number }) => {
  const trimmed = item.email.trim();

  return (
    <TableRow className={styles.tableRowTemplate}>
      <TableBodyCell className={styles.bodyCell}>
        <div>
          <span className={styles.emailCellHeader}>
            <span className={styles.emailCellTitle}>{getEmailNotificationLabel(rowIndex)}</span>
            {item.isCompanyEmail && <CompanyEmailTooltip />}
          </span>
          <br />
          {trimmed && <span>{item.email}</span>}
          {!trimmed && <span>—</span>}
        </div>
      </TableBodyCell>

      {STATUS_LABELS.map((statusLabel) => {
        const eventType = MAP_STATUS_LABEL_TO_EMAIL_NOTIFICATION_EVENT[statusLabel];
        const isEventEnabled = item.events.includes(eventType);

        return (
          <TableBodyCell key={statusLabel} className={styles.bodyCell}>
            {isEventEnabled ? <DoneDarkIcon /> : <CloseIcon />}
          </TableBodyCell>
        );
      })}
    </TableRow>
  );
});

const CompanyEmailNotificationTable = observer(() => {
  const { companyNotificationsStore } = useContext(RootStoreContext);
  const { companyNotifications } = companyNotificationsStore;

  const [isEditMode, setIsEditMode] = useState(false);
  const [initialState, setInitialState] = useState<EmailNotifications[]>([]);

  useEffect(() => {
    setInitialState(JSON.parse(JSON.stringify(companyNotificationsStore.companyNotifications)));
  }, [companyNotificationsStore.companyNotifications]);

  const handleChangeEmail = (rowIndex: number, newEmail: string) => {
    companyNotificationsStore.setEmail(rowIndex, newEmail);
  };

  const handleToggleEvent = (
    rowIndex: number,
    columnKey: keyof typeof MAP_STATUS_LABEL_TO_EMAIL_NOTIFICATION_EVENT,
  ) => {
    const event = MAP_STATUS_LABEL_TO_EMAIL_NOTIFICATION_EVENT[columnKey];

    companyNotificationsStore.toggleEvent(rowIndex, event);
  };

  const isAnyInvalidEmail = companyNotifications.some(
    (item) => item.email.trim() !== '' && !!validateEmail(item.email),
  );

  const handleSave = async () => {
    if (isAnyInvalidEmail) {
      return;
    }

    await companyNotificationsStore.saveChanges(initialState);
    setIsEditMode(false);
  };

  const handleCancel = () => {
    companyNotificationsStore.companyNotifications = JSON.parse(JSON.stringify(initialState));
    setIsEditMode(false);
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.header}>
        <div className={styles.headerLabel}>Notifications</div>
        {!isEditMode && (
          <Button onClick={() => setIsEditMode(true)} variant="link" className={styles.button}>
            <EditIcon /> Edit Settings
          </Button>
        )}
      </div>

      <Table className={styles.emailSettingsTable}>
        <TableHead className={styles.tableRowTemplate}>
          <TableHeadCell isSorted={false} currentSort={false}>
            Email
          </TableHeadCell>
          {STATUS_LABELS.map((statusLabel) => (
            <TableHeadCell key={statusLabel} isSorted={false} currentSort={false}>
              <div className={styles.tableHeaderCell}>
                {capitalize(statusLabel)}
                <Tooltip title={EMAIL_EVENTS_TOOLTIPS[statusLabel as StatusLabel]}>
                  <TooltipIcon />
                </Tooltip>
              </div>
            </TableHeadCell>
          ))}
        </TableHead>
        <TableBody>
          {companyNotifications.map((item, rowIndex) =>
            isEditMode ? (
              <EditableNotificationRow
                key={rowIndex}
                item={item}
                rowIndex={rowIndex}
                handleChangeEmail={handleChangeEmail}
                handleToggleEvent={handleToggleEvent}
              />
            ) : (
              <NotificationRow key={rowIndex} item={item} rowIndex={rowIndex} />
            ),
          )}
        </TableBody>
      </Table>

      {isEditMode && (
        <div className={styles.editButtons}>
          <Button onClick={handleSave} className={styles.saveButton}>
            Save Changes
          </Button>
          <Button className={styles.cancelButton} onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
});

export default CompanyEmailNotificationTable;
